import { useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { childErrorHospi, childrenGuaranteeIsRequired, isFraisGenRequired, isGarIncapRequired } from '@@viewHelpers/validations/guaranteesPrevTns.js';
import { addIndex, filter, forEach, propEq, propOr } from 'ramda';

const forEachIndexed = addIndex(forEach);

const useNiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm = ({
  form,
  guarantees,
  getMiddleText,
  getMiddleBoolean
}) => {
  const garIncap = form.watch('guarantees.OPTION_INCAP.selected');
  const garOptionZeroSinistre = form.watch('guarantees.OPTION_ZERO_SIN.selected');
  const garOptionExoneration = form.watch('guarantees.OPTION_EXO_COT.selected');
  const garOptionConfortPsy = form.watch('guarantees.OPTION_CONFORT_PSY.selected');
  const incapHospiChild = form.watch('guarantees.OPTION_HOSP_ENF_GAR.selected');
  const optionFraisGeneraux = form.watch('guarantees.OPTION_FR_GEN.selected');

  const watchedWarrantyFields = useWatch({
    control: form.control,
    name: filter(Boolean, [
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE') &&
        'guarantees.OPTION_FR_MAL.value',
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE') &&
        'guarantees.OPTION_FR_ACC.value',
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      ) && 'guarantees.OPTION_FR_HOSP.value',
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE') &&
        'guarantees.OPTION_FR_ALL.value',
      'guarantees.OPTION_INCAP_CAP.value'
    ])
  });

  // Frais généraux

  const watchedFraisGenFields = useWatch({
    control: form.control,
    name: filter(Boolean, [
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_MALADIE_EST_NECESSAIRE'
      ) && 'guarantees.OPTION_FR_GEN_FR_MAL.value',
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
      ) && 'guarantees.OPTION.FR_GEN_FR_ACC.value',
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      ) && 'guarantees.OPTION_FR_GEN_FR_HOSP.value',
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
      ) && 'guarantees.OPTION_FR_GEN_FR_TT_CAUSE.value',
      'guarantees.FR_GEN_CAP.value'
    ])
  });

  useEffect(() => {
    form.trigger('guarantees.OPTION_FR_GEN.selected');
  }, [watchedFraisGenFields, form]);

  useEffect(() => {
    form.trigger('guarantees.OPTION_INCAP.selected');
  }, [watchedWarrantyFields, form]);

  const {
    OPTION_INCAP,
    INCAP_MAD,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    OPTION_HOSP_ENF_GAR,
    HOSP_ENF_MAD,
    OPTION_FR_GEN,
    OPTION_INCAP_CAP,
    HOSP_ENF_ALLOW_AMOUNT
  } = guarantees;

  const incapCotisation = [
    {
      displayed: garIncap,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_INCAP_CAP),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_INCAP_CAP)
    },
    {
      displayed: garOptionZeroSinistre,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_ZERO_SIN),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_ZERO_SIN)
    },
    {
      displayed: garOptionExoneration,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_EXO_COT),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_EXO_COT)
    },
    {
      displayed: garOptionConfortPsy,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_CONFORT_PSY),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_CONFORT_PSY)
    },
    {
      displayed: incapHospiChild,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', HOSP_ENF_ALLOW_AMOUNT),
      yearlyAmount: propOr(0.0, 'annualPrice', HOSP_ENF_ALLOW_AMOUNT)
    },
    {
      displayed: optionFraisGeneraux,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_FR_GEN),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_FR_GEN)
    }
  ];

  const updateRadioButtonSelectedIndex = (guaranteeCode) => (value) => {
    const options = form.getValues(`guarantees.${guaranteeCode}.options`);

    forEachIndexed((option, index) => {
      form.setValue(
        `guarantees.${guaranteeCode}.options.${index}.selected`,
        propEq('value', value, option)
      );
    })(options);
  };

  return {
    optionFraisGeneraux,
    watchedFraisGenFields,
    watchedWarrantyFields,
    isFraisGenRequired,
    isGarIncapRequired,
    childrenGuaranteeIsRequired,
    childErrorHospi,
    garIncap,
    incapHospiChild,
    incapCotisation: filter(propEq('displayed', true), incapCotisation),
    OPTION_INCAP,
    INCAP_MAD,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    OPTION_HOSP_ENF_GAR,
    HOSP_ENF_MAD,
    OPTION_FR_GEN,
    HOSP_ENF_ALLOW_AMOUNT,
    updateRadioButtonSelectedIndex
  };
};

export default useNiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm;
